import { useEffect, useState } from "react";
import { FilterListItem } from "./FilterListItem";
import { STATIC_STRINGS } from "../../../../utils/Constants";
import FilterList from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import Menu from "@mui/material/Menu";
import Slider from "@mui/material/Slider";
import Tooltip from "@mui/material/Tooltip";
import GenericAutocomplete from "../../../../common/GenericAutocomplete";
import { FILTER_KEYS, SIZES } from "../../../../utils/enums";
import { getSkills } from "../../services";
import { useParams } from "react-router-dom";
import { FiltersType } from "../types";

const initialFilters = {
  [FILTER_KEYS.MARKS]: [0, 100],
  [FILTER_KEYS.PROGRESS_STATUS]: "",
  [FILTER_KEYS.USER_STATUS]: "",
  [FILTER_KEYS.TRUST_SCORE]: [0, 100],
  [FILTER_KEYS.STRONG_AREAS]: [],
};
const ProgressOptions = STATIC_STRINGS.PROGRESS_OPTIONS;
const StatusOptions = STATIC_STRINGS.STATUS_OPTIONS;

const getLabels = (selected: number[] = []) => {
  const labels = [
    { value: 0, label: "0%" },
    { value: 100, label: "100%" },
  ];

  selected.forEach((value) => {
    labels.push({
      value,
      label: `${value}%`,
    });
  });

  return labels;
};

export const FilterMenu = ({ applyFilters }) => {
  const { assessmentId } = useParams();
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [strongAreasOptions, setStrongAreasOptions] = useState<string[]>([]);
  const [filters, setFilters] = useState<FiltersType>(initialFilters);

  const updateFilters = <K extends FILTER_KEYS>(
    key: K,
    value: FiltersType[K]
  ) => {
    setFilters((prevFilters) => ({ ...prevFilters, [key]: value }));
  };

  const handleResetAll = () => {
    setFilters({ ...initialFilters });
    setFilterAnchorEl(null);
    applyFilters({}, true);
  };

  const handleApply = () => {
    applyFilters(filters);
    setFilterAnchorEl(null);
  };

  useEffect(() => {
    const getStrongAreas = async () => {
      try {
        const res = (await getSkills(assessmentId)) as string[];
        setStrongAreasOptions(res);
      } catch (error) {
        console.error("Error fetching skills:", error);
      }
    };
    getStrongAreas();
  }, [assessmentId]);

  return (
    <>
      <Tooltip title={STATIC_STRINGS.TOOLTIP_MESSAGES.SHOW_FILTERS}>
        <button
          className="btn btn-outline h-12 w-12 p-1 flex items-center justify-center text-neutral-dark"
          onClick={(e) => setFilterAnchorEl(e.currentTarget)}
        >
          <FilterList fontSize="medium" />
        </button>
      </Tooltip>
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <div className="w-96 p-2">
          <div
            className="w-full pb-2 border-b-2 border-b-neutral-dark/50"
            onClick={() => setFilterAnchorEl(null)}
          >
            <div className="w-80 mx-auto flex justify-between items-center">
              <div className="font-semibold flex justify-center items-center">
                <div className="text-neutral-dark mr-1">
                  <FilterList fontSize="medium" />
                </div>
                {STATIC_STRINGS.FILTERS}
              </div>
              <CloseIcon className="cursor-pointer hover:text-neutral-dark" />
            </div>
          </div>
          <FilterListItem
            filterName={STATIC_STRINGS.TOTAL_MARKS}
            resetHandler={() => {
              updateFilters(
                FILTER_KEYS.MARKS,
                initialFilters[FILTER_KEYS.MARKS]
              );
            }}
            Component={
              <Slider
                value={filters[FILTER_KEYS.MARKS]}
                marks={getLabels(filters[FILTER_KEYS.MARKS])}
                min={0}
                max={100}
                onChange={(e, newValue) =>
                  updateFilters(FILTER_KEYS.MARKS, newValue as number[])
                }
                sx={{ color: "var(--color-neutral-dark)" }}
              />
            }
          />
          <FilterListItem
            filterName={STATIC_STRINGS.PROGRESS}
            resetHandler={() => {
              updateFilters(
                FILTER_KEYS.PROGRESS_STATUS,
                initialFilters[FILTER_KEYS.PROGRESS_STATUS]
              );
            }}
            Component={
              <div className="flex justify-start items-center w-full">
                {ProgressOptions.map((status) => (
                  <div
                    key={status}
                    className={`p-2 mr-2 rounded-lg text-sm font-medium transition duration-300 border-2 border-neutral-dark text-primary hover:bg-neutral hover:text-white cursor-pointer ${
                      filters[FILTER_KEYS.PROGRESS_STATUS] === status &&
                      `text-white bg-neutral-dark`
                    }`}
                    onClick={() =>
                      updateFilters(FILTER_KEYS.PROGRESS_STATUS, status)
                    }
                  >
                    {status}
                  </div>
                ))}
              </div>
            }
          />
          <FilterListItem
            filterName={STATIC_STRINGS.USER_STATUS}
            resetHandler={() => {
              updateFilters(
                FILTER_KEYS.USER_STATUS,
                initialFilters[FILTER_KEYS.USER_STATUS]
              );
            }}
            Component={
              <div className="flex justify-start items-center w-full">
                {StatusOptions.map((status) => (
                  <div
                    key={status}
                    className={`p-2 mr-2 rounded-lg text-sm font-medium transition duration-300 border-2 border-neutral-dark text-primary hover:bg-neutral hover:text-white cursor-pointer ${
                      filters[FILTER_KEYS.USER_STATUS] === status &&
                      `text-white bg-neutral-dark`
                    }`}
                    onClick={() =>
                      updateFilters(FILTER_KEYS.USER_STATUS, status)
                    }
                  >
                    {status}
                  </div>
                ))}
              </div>
            }
          />
          <FilterListItem
            filterName={STATIC_STRINGS.TRUST_SCORE}
            resetHandler={() => {
              updateFilters(
                FILTER_KEYS.TRUST_SCORE,
                initialFilters[FILTER_KEYS.TRUST_SCORE]
              );
            }}
            Component={
              <Slider
                value={filters[FILTER_KEYS.TRUST_SCORE]}
                marks={getLabels(filters[FILTER_KEYS.TRUST_SCORE])}
                min={0}
                max={100}
                onChange={(e, newValue) =>
                  updateFilters(FILTER_KEYS.TRUST_SCORE, newValue as number[])
                }
                sx={{ color: "var(--color-neutral-dark)" }}
              />
            }
          />
          <FilterListItem
            filterName={STATIC_STRINGS.STRONG_AREAS}
            resetHandler={() => {
              updateFilters(
                FILTER_KEYS.STRONG_AREAS,
                initialFilters[FILTER_KEYS.STRONG_AREAS]
              );
            }}
            Component={
              <GenericAutocomplete
                size={SIZES.sm}
                onChangeHandler={(e, newValue) => {
                  updateFilters(FILTER_KEYS.STRONG_AREAS, newValue);
                }}
                options={strongAreasOptions}
                placeholder={STATIC_STRINGS.STRONG_AREAS_PLACEHOLDER}
                value={filters[FILTER_KEYS.STRONG_AREAS] || []}
              />
            }
          />
          <div className="w-full">
            <div className="w-80 mx-auto flex justify-between items-center pt-2">
              <button
                className="btn btn-outline px-2 py-1"
                onClick={handleResetAll}
              >
                {STATIC_STRINGS.RESET_ALL}
              </button>
              <button
                className="btn btn-primary px-3 py-1 border-2 border-neutral-dark"
                onClick={handleApply}
              >
                {STATIC_STRINGS.APPLY}
              </button>
            </div>
          </div>
        </div>
      </Menu>
    </>
  );
};
